import * as React from "react";
import { useState } from "react";
import { FormRow } from "../../components/FormRow";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import type { Field } from "../types";

interface Props {
  field: Field;
  preview: string;
  hasValue: boolean;
  /** True when DB value is empty but env var fallback is populated. */
  envSet?: boolean;
  onChange: (v: string) => void;
  onClear: () => void;
}

export function SecretField({
  field,
  preview,
  hasValue,
  envSet,
  onChange,
  onClear,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // Status label shown in display mode:
  //  - hasValue: masked preview from server
  //  - envSet (no DB value): "(using env value)"
  //  - otherwise: "(not set)"
  const statusLabel = hasValue
    ? preview
    : envSet
    ? "(using env value)"
    : "(not set)";
  const showEnvBadge = !hasValue && envSet;
  const envHint =
    showEnvBadge && !editing
      ? "env 변수로 설정되어 있습니다. DB에 저장하면 env를 override 합니다."
      : null;
  const helpParts = [envHint, field.help].filter(
    Boolean,
  ) as React.ReactNode[];
  const help = helpParts.length
    ? helpParts.map((part, i) => (
        <span key={i} className="block">
          {part}
        </span>
      ))
    : undefined;

  return (
    <FormRow
      label={field.title ?? field.key}
      required={field.required}
      help={help}
    >
      <div className="flex items-center gap-2">
        {!editing ? (
          <>
            {showEnvBadge && <Badge variant="info">env</Badge>}
            <code className="inline-block min-w-0 flex-1 truncate rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-sm text-muted-foreground">
              {statusLabel}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(true);
                setDraft("");
              }}
            >
              {showEnvBadge ? "Override" : "Change"}
            </Button>
          </>
        ) : (
          <>
            <Input
              className="max-w-md flex-1"
              type="password"
              revealable
              value={draft}
              autoFocus
              placeholder={
                envSet
                  ? "(override env value)"
                  : field.allow_empty
                  ? "(leave empty to clear)"
                  : ""
              }
              onChange={(e) => {
                setDraft(e.target.value);
                onChange(e.target.value);
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(false);
                setDraft("");
                onClear();
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </FormRow>
  );
}
